import { useEffect, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Activity, Loader2, Play, Trash2, Target, TrendingUp, TrendingDown, FlaskConical, Trophy,
  RefreshCw, Radar, Layers, SlidersHorizontal, Search, X, Sparkles, Check, Minus, BarChart3,
} from "lucide-react";

const fmtDay = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short" }) : "";

const fmtWhen = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-AU", {
        day: "numeric", month: "short", hour: "numeric", minute: "2-digit",
      })
    : "";

function LastRun({ run }) {
  if (!run || run.status === "running" || !run.finished_at) return null;
  const preview = run.config?.persist === false;
  const suffix =
    run.status === "interrupted"
      ? " · interrupted"
      : preview
      ? " · preview (not applied)"
      : " · applied to live";
  return (
    <div className="text-[11px] text-muted-foreground mt-1" data-testid="backtest-last-run">
      Last run: {fmtWhen(run.finished_at)}
      {suffix}
    </div>
  );
}

const STATUS_META = {
  pending: { label: "Pending", color: "#8E8E93" },
  won: { label: "Won", color: "#34C759" },
  lost: { label: "Lost", color: "#FF375F" },
  void: { label: "Void (DNP)", color: "#FF9500" },
};

function Stat({ label, value, tone }) {
  return (
    <div className="glass rounded-2xl p-4 text-center">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div
        className="font-display font-extrabold text-2xl mt-1"
        style={{ color: tone }}
        data-testid={`stat-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
      >
        {value}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Live tab */
function LiveTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/tracked-picks")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => load(), [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/tracked-picks/${id}`);
      load();
    } catch {
      toast.error("Couldn't remove pick");
    }
  };

  if (loading)
    return (
      <div className="h-40 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );

  const s = data?.summary || {};
  const picks = data?.picks || [];

  if (picks.length === 0)
    return (
      <div className="glass rounded-3xl p-12 text-center" data-testid="live-empty">
        <Target className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
        <h3 className="font-display text-xl font-bold">No tracked picks yet</h3>
        <p className="text-muted-foreground text-sm mt-1 max-w-md mx-auto">
          Tap <span className="font-semibold">Track</span> on any Value Finder or Best Bets pick.
          When the match finishes we grade it automatically and build your real ROI, hit rate and accuracy.
        </p>
      </div>
    );

  const roiTone = (s.roi_pct ?? 0) >= 0 ? "#34C759" : "#FF375F";

  return (
    <div className="space-y-5" data-testid="live-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Profit (u)" value={`${s.profit_units >= 0 ? "+" : ""}${s.profit_units ?? 0}`} tone={roiTone} />
        <Stat label="ROI" value={s.roi_pct == null ? "-" : `${s.roi_pct}%`} tone={roiTone} />
        <Stat label="Hit Rate" value={s.hit_rate == null ? "-" : `${s.hit_rate}%`} />
        <Stat label="Settled" value={`${s.settled ?? 0}/${s.total ?? 0}`} />
      </div>
      {s.settled > 0 && s.avg_predicted != null && (
        <p className="text-xs text-muted-foreground text-center">
          Accuracy check: these picks were predicted to win an average of <b>{s.avg_predicted}%</b> of the time,
          and they actually won <b>{s.hit_rate}%</b>.
        </p>
      )}

      {s.clv_tracked > 0 && (
        <div className="glass rounded-2xl p-4" data-testid="clv-summary">
          <div className="flex items-center justify-between mb-2">
            <div className="font-display font-bold text-sm">Beating the closing price</div>
            <span className="text-[11px] text-muted-foreground">{s.clv_tracked} picks with closing odds</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Avg CLV" value={s.avg_clv_pct == null ? "-" : `${s.avg_clv_pct > 0 ? "+" : ""}${s.avg_clv_pct}%`}
                  tone={(s.avg_clv_pct ?? 0) >= 0 ? "#34C759" : "#FF375F"} />
            <Stat label="Beat the Close" value={s.beat_close_rate == null ? "-" : `${s.beat_close_rate}%`}
                  tone={(s.beat_close_rate ?? 0) >= 50 ? "#34C759" : "#FF9500"} />
          </div>
          <p className="text-[11px] text-muted-foreground mt-2 leading-snug">
            A positive number means you took a better price than where the odds finished. That is the
            strongest sign of a real edge, no matter the short-term wins or losses.
          </p>
        </div>
      )}

      <div className="space-y-2.5">
        {picks.map((p, i) => {
          const sm = STATUS_META[p.status] || STATUS_META.pending;
          return (
            <motion.div
              key={`${p.fixture_id}-${p.prop_key}-${p.player}-${i}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: i * 0.02 }}
              className="glass rounded-2xl p-4 flex items-center gap-3"
              data-testid="tracked-pick-row"
            >
              <div
                className="w-1.5 self-stretch rounded-full shrink-0"
                style={{ backgroundColor: sm.color }}
              />
              <div className="min-w-0 flex-1">
                <div className="font-display font-bold truncate">{p.player}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {p.prop} · {p.home} v {p.away} · {fmtDay(p.match_date)}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs text-muted-foreground">
                  {p.predicted_pct}% {p.odd != null && `@${p.odd.toFixed(2)}`}
                </div>
                <Badge
                  className="rounded-full text-[10px] mt-0.5 border-0"
                  style={{ backgroundColor: sm.color + "22", color: sm.color }}
                >
                  {sm.label}
                  {p.status === "won" && ` +${p.pnl}`}
                  {p.status === "lost" && ` ${p.pnl}`}
                </Badge>
              </div>
              <button
                onClick={() => remove(p.id)}
                data-testid="delete-pick-btn"
                className="text-muted-foreground hover:text-destructive transition-colors shrink-0"
                title="Remove"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Backtest tab */
function CalibrationBar({ row }) {
  const has = row.n > 0 && row.actual != null;
  return (
    <div className="flex items-center gap-3 text-xs" data-testid="calib-row">
      <div className="w-16 text-muted-foreground shrink-0">{row.bucket}</div>
      <div className="flex-1 h-6 rounded-lg bg-secondary/50 relative overflow-hidden">
        {has && (
          <div
            className="absolute inset-y-0 left-0 rounded-lg"
            style={{
              width: `${Math.min(100, row.actual)}%`,
              backgroundColor:
                row.actual >= (row.predicted ?? 0) ? "#34C75955" : "#FF950055",
            }}
          />
        )}
        {has && (
          <div className="absolute inset-0 flex items-center justify-between px-2 font-semibold">
            <span>actual {row.actual}%</span>
            <span className="text-muted-foreground">pred {row.predicted}%</span>
          </div>
        )}
        {!has && (
          <div className="absolute inset-0 flex items-center px-2 text-muted-foreground">
            no samples
          </div>
        )}
      </div>
      <div className="w-10 text-right text-muted-foreground shrink-0">n={row.n}</div>
    </div>
  );
}

const DEFAULT_BT_CONFIG = { leagues: [], n_seasons: 3, cap: null, persist: true };

function usePersistentConfig(key) {
  const [cfg, setCfg] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? { ...DEFAULT_BT_CONFIG, ...JSON.parse(raw) } : DEFAULT_BT_CONFIG;
    } catch {
      return DEFAULT_BT_CONFIG;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(cfg));
    } catch {
      /* ignore quota / private-mode errors */
    }
  }, [key, cfg]);
  return [cfg, setCfg];
}

function BacktestConfig({ value, onChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const timer = useRef();

  const search = (text) => {
    setQ(text);
    clearTimeout(timer.current);
    if (text.trim().length < 2) {
      setResults([]);
      return;
    }
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/tournaments/search", { params: { q: text.trim() } });
        setResults(data || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
  };

  const addLeague = (lg) => {
    if (!value.leagues.some((l) => l.id === lg.id))
      onChange({ ...value, leagues: [...value.leagues, { id: lg.id, name: lg.name }] });
    setQ("");
    setResults([]);
  };
  const removeLeague = (id) =>
    onChange({ ...value, leagues: value.leagues.filter((l) => l.id !== id) });

  return (
    <div className="rounded-xl bg-secondary/40 p-3.5 space-y-3" data-testid="backtest-config">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        <SlidersHorizontal className="h-3.5 w-3.5" /> Scope
      </div>

      {/* League search */}
      <div className="relative">
        <div className="flex items-center gap-2 rounded-lg bg-card px-3 h-9 border border-border">
          <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <input
            value={q}
            onChange={(e) => search(e.target.value)}
            data-testid="backtest-league-search"
            placeholder="Search a competition to add (e.g. Championship, MLS)…"
            className="bg-transparent outline-none text-sm w-full"
          />
          {searching && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
        </div>
        {results.length > 0 && (
          <div className="absolute z-20 mt-1 w-full max-h-56 overflow-auto rounded-xl glass border border-border shadow-lg p-1"
               data-testid="backtest-league-results">
            {results.map((lg) => (
              <button
                key={lg.id}
                onClick={() => addLeague(lg)}
                data-testid="backtest-league-option"
                className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-secondary text-left text-sm"
              >
                {lg.logo && <img src={lg.logo} alt="" className="h-4 w-4 object-contain" />}
                <span className="font-medium truncate">{lg.name}</span>
                <span className="text-xs text-muted-foreground truncate ml-auto">{lg.country}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Selected leagues */}
      {value.leagues.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">
          Nothing selected. We will use the 7 major leagues (EPL, La Liga, Serie A, Bundesliga,
          Ligue 1, Brazil, World Cup).
        </p>
      ) : (
        <div className="flex flex-wrap gap-1.5" data-testid="backtest-selected-leagues">
          {value.leagues.map((l) => (
            <span key={l.id}
                  className="flex items-center gap-1 text-[11px] rounded-full bg-primary/15 text-primary px-2 py-0.5 font-medium">
              {l.name}
              <button onClick={() => removeLeague(l.id)} data-testid="backtest-remove-league"
                      className="hover:text-destructive">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Seasons + cap */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] text-muted-foreground">Seasons back</label>
          <Input
            type="number" min={1} max={6}
            value={value.n_seasons}
            data-testid="backtest-seasons"
            onChange={(e) => onChange({ ...value, n_seasons: Math.max(1, Math.min(6, Number(e.target.value) || 1)) })}
            className="h-9 rounded-lg mt-0.5"
          />
        </div>
        <div>
          <label className="text-[11px] text-muted-foreground">Max fixtures (min 50, blank = all)</label>
          <Input
            type="number" min={50}
            value={value.cap ?? ""}
            data-testid="backtest-cap"
            placeholder="All"
            onChange={(e) => {
              const n = Number(e.target.value);
              onChange({ ...value, cap: e.target.value === "" || !n ? null : Math.max(50, n) });
            }}
            className="h-9 rounded-lg mt-0.5"
          />
        </div>
      </div>

      {/* Persist toggle */}
      <div className="flex items-start justify-between gap-3 pt-1">
        <div className="min-w-0">
          <div className="text-sm font-medium">Apply results to the live model</div>
          <p className="text-[11px] text-muted-foreground">
            On: the model updates its calibration from this run. Off: preview only. It shows the
            numbers but does not change live predictions.
          </p>
        </div>
        <Switch
          checked={value.persist}
          onCheckedChange={(v) => onChange({ ...value, persist: v })}
          data-testid="backtest-persist"
        />
      </div>
    </div>
  );
}

function ResultsBacktestCard() {
  const [run, setRun] = useState(null);
  const [starting, setStarting] = useState(false);
  const [cfg, setCfg] = usePersistentConfig("bt_cfg_results");

  const poll = useCallback(() => {
    api.get("/backtest/results/latest").then((r) => setRun(r.data)).catch(() => {});
  }, []);
  useEffect(() => { poll(); }, [poll]);
  useEffect(() => {
    if (run?.status !== "running") return;
    const t = setInterval(poll, 4000);
    return () => clearInterval(t);
  }, [poll, run?.status]);

  const start = async () => {
    setStarting(true);
    try {
      await api.post("/backtest/results/run", cfg);
      toast.success(cfg.persist
        ? "Match-results backtest started, runs in the background"
        : "Preview backtest started, live model won't be changed");
      poll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setStarting(false);
    }
  };

  const running = run?.status === "running";
  const rep = run?.report;
  const pct = run?.total ? Math.round((100 * (run.processed || 0)) / run.total) : 0;
  const wasPreview = rep && rep.persisted === false;

  return (
    <div className="glass rounded-2xl p-4 space-y-3" data-testid="results-backtest-card">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="font-display font-bold">Match-Result Model Backtest</div>
          <p className="text-xs text-muted-foreground">
            Tests how often the model calls the right result (home, draw or away) on past matches,
            and tunes the confidence shown on the live Match Prediction card. Pick the scope below.
          </p>
          <LastRun run={run} />
        </div>
        <Button onClick={start} disabled={running || starting} data-testid="run-results-backtest-btn" className="rounded-full">
          {running || starting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Play className="h-4 w-4 mr-1.5" />}
          {running ? "Running…" : "Run Results Backtest"}
        </Button>
      </div>

      {!running && <BacktestConfig value={cfg} onChange={setCfg} />}

      {running && (
        <div data-testid="results-backtest-progress">
          <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
            <span>Grading matches…</span>
            <span>{run.processed || 0}/{run.total || "?"}</span>
          </div>
          <div className="h-2 rounded-full bg-secondary overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      {rep && rep.n >= 200 && !running && (
        <>
          {wasPreview && (
            <div className="text-[11px] rounded-lg bg-[#FF9500]/15 text-[#FF9500] px-2.5 py-1.5 font-medium"
                 data-testid="results-preview-note">
              Preview only. These numbers were NOT applied to the live model.
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center" data-testid="results-backtest-metrics">
            {[["Matches", rep.n], ["Result accuracy", `${rep.accuracy}%`], ["Error score", rep.rps_cal], ["Calibration", rep.T]].map(([l, v]) => (
              <div key={l} className="rounded-xl bg-secondary/40 py-2">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{l}</div>
                <div className="font-display font-bold">{v}</div>
              </div>
            ))}
          </div>
          {rep.by_league && (
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(rep.by_league).map(([k, v]) => (
                <span key={k} className="text-[11px] rounded-full px-2 py-0.5 bg-secondary/60 font-medium">{k}: {v}</span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function BacktestTab() {
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [cfg, setCfg] = usePersistentConfig("bt_cfg_props");

  const poll = useCallback(() => {
    api
      .get("/backtest/latest")
      .then((r) => setRun(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    poll();
  }, [poll]);

  useEffect(() => {
    if (run?.status !== "running") return;
    const t = setInterval(poll, 4000);
    return () => clearInterval(t);
  }, [poll, run?.status]);

  const start = async () => {
    setStarting(true);
    try {
      await api.post("/backtest/run", cfg);
      toast.success(cfg.persist
        ? "Backtest started, this runs in the background"
        : "Preview backtest started, live model won't be changed");
      poll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setStarting(false);
    }
  };

  if (loading)
    return (
      <div className="h-40 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );

  const running = run?.status === "running";
  const report = run?.report;
  const ov = report?.overall;
  const pct = run?.total ? Math.round((100 * (run.processed || 0)) / run.total) : 0;

  return (
    <div className="space-y-5" data-testid="backtest-tab">
      <ResultsBacktestCard />
      <div className="glass rounded-2xl p-4 space-y-3">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="font-display font-bold">Model Backtest (player props)</div>
            <p className="text-xs text-muted-foreground">
              Checks how accurate player-prop predictions were on past games and tunes each
              market's calibration. Pick the scope below. Note: wide scopes download a lot of data.
            </p>
            <LastRun run={run} />
          </div>
          <Button
            onClick={start}
            disabled={running || starting}
            data-testid="run-backtest-btn"
            className="rounded-full"
          >
            {running || starting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
            ) : (
              <Play className="h-4 w-4 mr-1.5" />
            )}
            {running ? "Running…" : "Run Backtest"}
          </Button>
        </div>
        {!running && <BacktestConfig value={cfg} onChange={setCfg} />}
      </div>

      {running && (
        <div className="glass rounded-2xl p-4" data-testid="backtest-progress">
          <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
            <span>Processing fixtures…</span>
            <span>
              {run.processed || 0}/{run.total || "?"}
            </span>
          </div>
          <div className="h-2 rounded-full bg-secondary overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      {!running && report && ov && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Predictions" value={ov.total_cards} />
            <Stat label="Graded" value={ov.n} />
            <Stat label="Accuracy" value={ov.hit_rate == null ? "-" : `${ov.hit_rate}%`} />
            <Stat label="Brier" value={ov.brier ?? "-"} />
          </div>
          <p className="text-xs text-muted-foreground text-center">
            These props were predicted to hit an average of <b>{ov.avg_pred}%</b> and actually hit{" "}
            <b>{ov.hit_rate}%</b>. The closer those two numbers, the more you can trust the percentages.
            Did-not-play rate <b>{ov.dnp_rate}%</b> (players who did not feature; lineup filtering reduces this).
          </p>

          {report.calibration_fit && report.calibration_fit.n > 0 && (
            <div
              className={"glass rounded-2xl p-4 border " +
                (report.calibration_fit.persisted === false ? "border-[#FF9500]/40" : "border-[#34C759]/30")}
              data-testid="calibration-card"
            >
              <div className="text-sm font-semibold mb-2 flex items-center gap-2">
                <Target className={"h-4 w-4 " + (report.calibration_fit.persisted === false ? "text-[#FF9500]" : "text-[#34C759]")} />
                {report.calibration_fit.persisted === false ? "Calibration preview (not applied)" : "Calibration correction (live)"}
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                {report.calibration_fit.persisted === false
                  ? "This was a preview run. The numbers below are NOT applied to live predictions."
                  : "A correction learned from this backtest is now applied to every prediction, so the percentages you see match how often props actually hit."}
              </p>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="rounded-xl bg-secondary/50 py-2.5">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    Raw avg
                  </div>
                  <div className="font-display font-bold text-lg">
                    {report.calibration_fit.raw_avg}%
                  </div>
                </div>
                <div className="rounded-xl bg-[#34C759]/10 py-2.5">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    Calibrated
                  </div>
                  <div className="font-display font-bold text-lg text-[#34C759]">
                    {report.calibration_fit.cal_avg}%
                  </div>
                </div>
                <div className="rounded-xl bg-secondary/50 py-2.5">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    Actual
                  </div>
                  <div className="font-display font-bold text-lg">
                    {report.calibration_fit.actual}%
                  </div>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-2.5 text-center">
                Error score improved {report.calibration_fit.raw_brier} to{" "}
                <b className="text-[#34C759]">{report.calibration_fit.cal_brier}</b> (lower is sharper).
                Fitted on {report.calibration_fit.n} settled props.
              </p>

              {report.calibration_fit.holdout && (
                <div className="mt-3 pt-3 border-t border-border" data-testid="holdout-block">
                  <div className="text-[11px] font-semibold mb-1.5">
                    Fairness test: trained on 70% of the data, then scored on {report.calibration_fit.holdout.n_test} props it had never seen. Lower error wins.
                  </div>
                  {(() => {
                    const h = report.calibration_fit.holdout;
                    const opts = [
                      { k: "raw_brier", label: "No calibration" },
                      { k: "global_brier", label: "Global curve" },
                      { k: "market_brier", label: "Per-market" },
                    ];
                    const best = Math.min(...opts.map((o) => h[o.k]));
                    return (
                      <>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        {opts.map((o) => {
                          const win = h[o.k] === best;
                          return (
                            <div
                              key={o.k}
                              className={"rounded-xl py-2 " + (win ? "bg-[#34C759]/15" : "bg-secondary/40")}
                              data-testid={`holdout-${o.k}`}
                            >
                              <div className="text-[9px] uppercase tracking-wide text-muted-foreground">
                                {o.label}
                              </div>
                              <div
                                className={"font-display font-bold text-sm " + (win ? "text-[#34C759]" : "")}
                              >
                                {h[o.k]}
                                {win && " ✓"}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-2">
                        {h.market_brier < h.global_brier
                          ? "Per-market corrections win on unseen props, so the extra detail is paying off at this data volume."
                          : "The global correction wins on unseen props. Per-market fits are not beating it yet, so they stay anchored to the global one. More data (leagues or seasons) may change this."}
                      </p>
                      </>
                    );
                  })()}
                </div>
              )}

              {report.calibration_fit.markets &&
                Object.keys(report.calibration_fit.markets).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5" data-testid="market-curves">
                    {Object.entries(report.calibration_fit.markets).map(([k, m]) => (
                      <span
                        key={k}
                        className="text-[10px] rounded-full px-2 py-0.5 bg-secondary/60 text-muted-foreground"
                      >
                        {m.label || k}: {m.n}
                      </span>
                    ))}
                  </div>
                )}
            </div>
          )}

          <div className="glass rounded-2xl p-4 space-y-2">
            <div className="text-sm font-semibold mb-1 flex items-center gap-2">
              <Target className="h-4 w-4 text-primary" /> Predicted vs actual, by confidence
            </div>
            {report.calibration.map((row) => (
              <CalibrationBar key={row.bucket} row={row} />
            ))}
          </div>

          <div className="glass rounded-2xl p-4">
            <div className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Trophy className="h-4 w-4 text-primary" /> Accuracy by risk tier
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              {["low", "medium", "high"].map((t) => {
                const r = report.by_risk[t] || {};
                return (
                  <div key={t} className="rounded-xl bg-secondary/50 py-3">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground capitalize">
                      {t} risk
                    </div>
                    <div className="font-display font-extrabold text-xl mt-0.5 flex items-center justify-center gap-1">
                      {r.hit_rate == null ? "-" : `${r.hit_rate}%`}
                      {r.hit_rate != null && r.avg_pred != null && (
                        r.hit_rate >= r.avg_pred ? (
                          <TrendingUp className="h-3.5 w-3.5 text-[#34C759]" />
                        ) : (
                          <TrendingDown className="h-3.5 w-3.5 text-[#FF9500]" />
                        )
                      )}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      pred {r.avg_pred ?? "-"}% · n={r.n ?? 0}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {!running && !report && (
        <div className="glass rounded-3xl p-12 text-center" data-testid="backtest-empty">
          <FlaskConical className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-display text-xl font-bold">No backtest yet</h3>
          <p className="text-muted-foreground text-sm mt-1">
            Run one to see how the model's predictions held up against real past results.
          </p>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Ledger tab */
const LEDGER_STATUS = {
  won: { label: "Won", color: "#34C759" },
  lost: { label: "Lost", color: "#FF375F" },
  void: { label: "Void (DNP)", color: "#FF9500" },
};

function LedgerTab() {
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sweeping, setSweeping] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get("/ledger/summary"), api.get("/ledger/recent")])
      .then(([s, r]) => {
        setSummary(s.data);
        setRecent(r.data?.picks || []);
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => load(), [load]);

  const runSweep = async () => {
    setSweeping(true);
    try {
      await api.post("/ledger/sweep");
      toast.success("Sweep started. Logging predictions for upcoming fixtures.");
      setTimeout(load, 3000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSweeping(false);
    }
  };

  if (loading)
    return (
      <div className="h-40 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );

  const ov = summary?.overall || {};
  const profitTone = (ov.profit_units ?? 0) >= 0 ? "#34C759" : "#FF375F";

  const SweepButton = (
    <Button onClick={runSweep} disabled={sweeping} data-testid="ledger-sweep-btn" className="rounded-full">
      {sweeping ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
      {sweeping ? "Sweeping…" : "Run Sweep"}
    </Button>
  );

  if (!ov.total)
    return (
      <div className="glass rounded-3xl p-12 text-center" data-testid="ledger-empty">
        <Radar className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
        <h3 className="font-display text-xl font-bold">No predictions logged yet</h3>
        <p className="text-muted-foreground text-sm mt-1 mb-5 max-w-md mx-auto">
          The ledger saves a snapshot of every match and player-prop prediction for upcoming
          fixtures, then grades them automatically once results are in. Those real outcomes feed
          back into the model. Start the first sweep now.
        </p>
        {SweepButton}
      </div>
    );

  return (
    <div className="space-y-5" data-testid="ledger-tab">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <p className="text-xs text-muted-foreground max-w-lg">
          Every prediction is saved when it is made and graded automatically after the match. This
          is the model's real-world track record. The closer <b>predicted</b> sits to <b>actual</b>,
          the more you can trust the percentages.
        </p>
        {SweepButton}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Logged" value={ov.total ?? 0} />
        <Stat label="Pending" value={ov.pending ?? 0} tone="#8E8E93" />
        <Stat label="Graded" value={ov.graded ?? 0} />
        <Stat label="Hit Rate" value={ov.hit_rate == null ? "-" : `${ov.hit_rate}%`} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Stat
          label="Profit (u)"
          value={`${(ov.profit_units ?? 0) >= 0 ? "+" : ""}${ov.profit_units ?? 0}`}
          tone={profitTone}
        />
        <Stat label="ROI" value={ov.roi_pct == null ? "-" : `${ov.roi_pct}%`} tone={profitTone} />
      </div>

      {/* Match vs Props */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[
          { key: "match", label: "Match Markets", data: summary?.match },
          { key: "props", label: "Player Props", data: summary?.props },
        ].map(({ key, label, data }) => {
          const d = data || {};
          return (
            <div key={key} className="glass rounded-2xl p-4" data-testid={`ledger-${key}-card`}>
              <div className="flex items-center gap-2 mb-2">
                <Layers className="h-4 w-4 text-primary" />
                <span className="font-display font-bold text-sm">{label}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-xl bg-secondary/40 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Graded</div>
                  <div className="font-display font-bold">{d.graded ?? 0}</div>
                </div>
                <div className="rounded-xl bg-secondary/40 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Hit</div>
                  <div className="font-display font-bold">{d.hit_rate == null ? "-" : `${d.hit_rate}%`}</div>
                </div>
                <div className="rounded-xl bg-secondary/40 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Pred</div>
                  <div className="font-display font-bold">{d.avg_pred == null ? "-" : `${d.avg_pred}%`}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Calibration */}
      {summary?.calibration?.length > 0 && (
        <div className="glass rounded-2xl p-4 space-y-2" data-testid="ledger-calibration">
          <div className="text-sm font-semibold mb-1 flex items-center gap-2">
            <Target className="h-4 w-4 text-primary" /> Predicted vs actual (props)
          </div>
          {summary.calibration.map((row) => (
            <CalibrationBar key={row.bucket} row={row} />
          ))}
        </div>
      )}

      {/* By market */}
      {summary?.by_market?.length > 0 && (
        <div className="glass rounded-2xl p-4" data-testid="ledger-by-market">
          <div className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Trophy className="h-4 w-4 text-primary" /> Accuracy by market
          </div>
          <div className="space-y-1.5">
            {summary.by_market
              .filter((m) => m.graded > 0)
              .sort((a, b) => (b.hit_rate ?? 0) - (a.hit_rate ?? 0))
              .map((m) => (
                <div
                  key={m.market}
                  className="flex items-center justify-between text-xs rounded-xl bg-secondary/40 px-3 py-2"
                  data-testid="ledger-market-row"
                >
                  <span className="font-medium truncate">{m.market}</span>
                  <span className="flex items-center gap-3 text-muted-foreground shrink-0">
                    <span>n={m.graded}</span>
                    <span>pred {m.avg_pred ?? "-"}%</span>
                    <span
                      className="font-display font-bold text-sm"
                      style={{ color: (m.hit_rate ?? 0) >= (m.avg_pred ?? 0) ? "#34C759" : "#FF9500" }}
                    >
                      {m.hit_rate == null ? "-" : `${m.hit_rate}%`}
                    </span>
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Recently graded */}
      {recent.length > 0 && (
        <div className="space-y-2.5">
          <div className="text-sm font-semibold flex items-center gap-2">
            <Activity className="h-4 w-4 text-primary" /> Recently graded
          </div>
          {recent.map((p, i) => {
            const sm = LEDGER_STATUS[p.status] || LEDGER_STATUS.void;
            return (
              <div
                key={p.id || i}
                className="glass rounded-2xl p-3.5 flex items-center gap-3"
                data-testid="ledger-recent-row"
              >
                <div className="w-1.5 self-stretch rounded-full shrink-0" style={{ backgroundColor: sm.color }} />
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold truncate text-sm">
                    {p.player ? `${p.player} · ${p.selection}` : p.selection}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {p.market} · {p.home} v {p.away}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs text-muted-foreground">
                    {p.predicted_pct}% {p.odd != null && `@${Number(p.odd).toFixed(2)}`}
                  </div>
                  <Badge
                    className="rounded-full text-[10px] mt-0.5 border-0"
                    style={{ backgroundColor: sm.color + "22", color: sm.color }}
                  >
                    {sm.label}
                    {p.status === "won" && p.pnl != null && ` +${p.pnl}`}
                    {p.status === "lost" && p.pnl != null && ` ${p.pnl}`}
                  </Badge>
                  {p.actual != null && (
                    <div className="text-[10px] text-muted-foreground mt-0.5">actual: {String(p.actual)}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Page */
/* ------------------------------------------------------------------ Pitchie AI tab */
const PredResultIcon = ({ ok }) =>
  ok ? <Check className="h-4 w-4 text-[#34C759]" /> : <X className="h-4 w-4 text-[#FF3B30]" />;

function PitchieTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/ai-predictions/history")
      .then((r) => setData(r.data))
      .catch(() => setData({ stats: {}, predictions: [] }))
      .finally(() => setLoading(false));
  }, []);

  if (loading)
    return (
      <div className="glass rounded-3xl p-12 flex justify-center" data-testid="pitchie-loading">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );

  const s = data?.stats || {};
  const preds = data?.predictions || [];
  const resultName = (p) =>
    p.predicted.result === "Home" ? p.home : p.predicted.result === "Away" ? p.away : "Draw";
  const actualName = (p) =>
    p.grade.match.actual_result === "Home" ? p.home
      : p.grade.match.actual_result === "Away" ? p.away : "Draw";

  const catLabels = {
    goal_scorers: "Goal Scorers", shots_on_target: "Shots on Target",
    shots: "Shots", tackles: "Tackles",
  };

  return (
    <div className="space-y-5" data-testid="pitchie-tab">
      <div className="glass rounded-2xl p-4 flex items-start gap-3" data-testid="pitchie-intro">
        <div className="h-10 w-10 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
          <Sparkles className="h-5 w-5 text-primary" />
        </div>
        <div>
          <p className="font-display font-bold">Pitchie AI predictions</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Every prediction Pitchie AI makes is recorded here and graded automatically after
            full time, so you can see how the blind AI really performs over time.
          </p>
        </div>
      </div>

      {s.games > 0 && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="pitchie-stats">
            <Stat label="Graded Games" value={s.games ?? 0} />
            <Stat label="Result %" value={s.result_pct == null ? "-" : `${s.result_pct}%`}
                  tone={s.result_pct >= 50 ? "up" : "down"} />
            <Stat label="Exact Score %" value={s.score_pct == null ? "-" : `${s.score_pct}%`} />
            <Stat label="Props %" value={s.props?.pct == null ? "-" : `${s.props.pct}%`}
                  tone={s.props?.pct >= 50 ? "up" : "down"} />
          </div>
          <div className="glass rounded-2xl p-4" data-testid="pitchie-categories">
            <p className="text-xs font-semibold text-muted-foreground mb-3 flex items-center gap-1.5">
              <BarChart3 className="h-3.5 w-3.5 text-primary" /> Player prop hit rate by market
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {Object.entries(catLabels).map(([k, label]) => {
                const c = s.categories?.[k] || {};
                return (
                  <div key={k} className="text-center rounded-xl bg-secondary/40 p-3">
                    <p className="font-display font-extrabold text-xl">
                      {c.pct == null ? "-" : `${c.pct}%`}
                    </p>
                    <p className="text-[11px] text-muted-foreground">{label}</p>
                    <p className="text-[10px] text-muted-foreground">{c.hits ?? 0}/{c.total ?? 0}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {preds.length === 0 ? (
        <div className="glass rounded-3xl p-12 text-center" data-testid="pitchie-empty">
          <Sparkles className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <h3 className="font-display text-xl font-bold">No AI predictions yet</h3>
          <p className="text-muted-foreground text-sm mt-1">
            Open any fixture under Competitions and Pitchie AI will make a prediction. It shows up
            here and gets graded once the game finishes.
          </p>
        </div>
      ) : (
        <div className="space-y-2.5" data-testid="pitchie-list">
          {preds.map((p) => (
            <div key={p.fixture_id} className="glass rounded-2xl p-4" data-testid="pitchie-row">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-display font-bold truncate">{p.home} vs {p.away}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {p.competition}{p.kickoff ? ` · ${fmtWhen(p.kickoff)}` : ""}
                  </p>
                </div>
                <span className={
                  "text-[11px] font-semibold rounded-full px-2.5 py-1 shrink-0 " +
                  (p.status === "graded" ? "bg-secondary text-foreground" : "bg-[#FF9500]/15 text-[#FF9500]")
                } data-testid="pitchie-status">
                  {p.status === "graded" ? "Graded" : "Pending"}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-2.5 text-sm">
                <span className="rounded-full bg-primary/10 text-primary font-semibold px-2.5 py-0.5 text-xs">
                  AI: {resultName(p)} {p.predicted.score}
                </span>
                {p.predicted.confidence && (
                  <span className="text-[11px] text-muted-foreground">{p.predicted.confidence} conf.</span>
                )}
                {p.status === "graded" && (
                  <>
                    <span className="flex items-center gap-1 text-xs font-semibold">
                      <PredResultIcon ok={p.grade.match.result_correct} />
                      Actual {p.grade.match.actual_score} ({actualName(p)})
                    </span>
                    <span className="text-[11px] rounded-full bg-secondary px-2 py-0.5 text-muted-foreground">
                      Props {p.grade.counts.props_hit}/{p.grade.counts.props_total} hit
                    </span>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TrackRecord() {
  const [tab, setTab] = useState("live");
  return (
    <div className="space-y-6" data-testid="track-record-page">
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-2xl bg-primary/15 flex items-center justify-center shrink-0">
          <Activity className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
            Track Record
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            See how the model performs for real. Live picks track actual ROI, and the backtest checks accuracy against past results.
          </p>
        </div>
      </div>

      <div className="flex gap-1 bg-secondary/60 rounded-full p-1 w-fit">
        {[
          { id: "live", label: "Live Picks" },
          { id: "pitchie", label: "Pitchie AI" },
          { id: "backtest", label: "Backtest" },
          { id: "ledger", label: "Ledger" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={
              "px-4 py-1.5 rounded-full text-sm font-medium transition-colors " +
              (tab === t.id ? "bg-card text-foreground shadow-sm" : "text-muted-foreground")
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "live" && <LiveTab />}
      {tab === "pitchie" && <PitchieTab />}
      {tab === "backtest" && <BacktestTab />}
      {tab === "ledger" && <LedgerTab />}
    </div>
  );
}
